import type {
  DSAttendanceDoctorSuggestion,
  DSAttendanceDoctorSuggestionField
} from '@/Network/Types/Responses/DsAttendancePolicyResponses'
import { formatCrm } from './crm-format'

/**
 * RF-03/RF-04 do PRD 32 — quem tem autocomplete e o que a seleção grava na linha.
 *
 * As colunas de CRM e nome do médico são identificadas pelo **mesmo mapa** que as leva
 * ao payload de conciliação, nunca por heurística nova: label que já não entra no payload
 * também não ganha autocomplete. Daí sai de graça o "independe da posição das colunas" da
 * RF-04 — a coluna par do cruzamento é achada pelo mesmo critério.
 */

export type SuggestionColumn = {
  key: string
  field: DSAttendanceDoctorSuggestionField
}

const SUGGESTION_FIELD_BY_PAYLOAD_FIELD: Record<string, DSAttendanceDoctorSuggestionField> = {
  rawCrm: 'crm',
  rawDoctorName: 'name'
}

/** A chave da coluna vem do label do modelo de folha; o mapa do payload é sem acento. */
export function normalizeColumnKey(key: string): string {
  return key
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
}

/**
 * Colunas do modelo de folha que oferecem autocomplete, na ordem em que aparecem.
 * Modelo com só uma das duas colunas devolve só ela — sem caso especial.
 */
export function resolveSuggestionColumns(
  columns: { key: string }[],
  payloadFieldByColumnKey: Record<string, string>
): SuggestionColumn[] {
  return columns.flatMap((column) => {
    const payloadField = payloadFieldByColumnKey[normalizeColumnKey(column.key)]
    const field = payloadField ? SUGGESTION_FIELD_BY_PAYLOAD_FIELD[payloadField] : undefined
    return field ? [{ key: column.key, field }] : []
  })
}

/**
 * RF-04 — a seleção grava CRM e nome da mesma linha numa única ação, com **overwrite
 * incondicional**: a seleção é intencional em qualquer grau de divergência. O CRM entra
 * no formato canônico `12345-SP`.
 *
 * RN-04 — o efeito é exclusivamente textual: nenhum identificador do profissional é
 * gravado, e a linha não fica vinculada ao registro do cadastro.
 */
export function fillRowFromSuggestion(
  row: Record<string, string | boolean>,
  columns: SuggestionColumn[],
  suggestion: DSAttendanceDoctorSuggestion
): void {
  for (const column of columns) {
    row[column.key] = column.field === 'crm' ? formatCrm(suggestion) : suggestion.name
  }
}
